/**
 * @module Date Picker 
 * @author: UI Architecture Team @ZS
 */
import Field from '../field/field.m.js';

function isThing(elem) {
    if (elem && (elem.length || document.body.contains(elem))) {
        return true;
    }

    return false;
}
function isVisible(elem) {
    return !(elem.offsetWidth <= 0 && elem.offsetHeight <= 0)
}

class DatePicker extends Field {
    constructor(...$) {
        const _ = super(...$);

        return _;
    }
    setup() {
        super.setup();

        // Initialize Field

        this._setDefaults();

        // Sync Props
        this.syncProp('flippable');
        this.syncProp('hideOnScroll');
        this.syncProp('firstDayOfWeek');
        this.syncProp('minDate');
        this.syncProp('maxDate');
        this.syncProp('months');
        this.syncProp('daysOfWeek');
        this.syncProp('dateValue');
        this.syncProp('mode');
        this.syncProp('disabledDates');

        // Polyfill to support closest
        if (!Element.prototype.matches) {
            Element.prototype.matches = Element.prototype.msMatchesSelector ||
                Element.prototype.webkitMatchesSelector;
        }

        if (!Element.prototype.closest) {
            Element.prototype.closest = function (s) {
                var el = this;

                do {
                    if (el.matches(s)) return el;
                    el = el.parentElement || el.parentNode;
                } while (el !== null && el.nodeType === 1);
                return null;
            };
        }
    }
    static get observedAttributes() {
        return super.observedAttributes.concat(this.ownAttributes);
    }

    static get ownAttributes() {
        return ['flippable', 'hide-on-scroll', 'first-day-of-week', 'mode'/*, 'min-date', 'max-date', 'date-value', 'disabled-dates'*/];
    }

    propertyChangedCallback(name, oldValue, newValue) {
        this.mode === 'classic' ? console.warn("Classic mode has been deprecated since v5.0 and shall be removed in future release. Please use 'action' mode instead.") : null;

        if (!this._isRendered) {
            return;
        }
        switch (name) {
            case 'minDate':
            case 'maxDate':
            case 'firstDayOfWeek':
            case 'daysOfWeek':
            case 'months':
            case 'disabledDates':
                this.update();
                break;
            case 'dateValue':
                this.value = this.dateToString(this.dateValue);
                break;
            case 'flippable':
                this.setContainerPosition();
                break;
            case 'hideOnScroll':
                this.toggleHideOnScroll();
                break;
            case 'mode':
                this.mode ? this.container.setAttribute('mode', this.mode) : this.container.removeAttribute('mode');
                this.update();
                break;
            default:
                super.propertyChangedCallback(name, oldValue, newValue);
        }
    }

    getPropertyType(name) {
        switch (name) {
            case 'flippable':
            case 'hide-on-scroll':
                return 'boolean';
            case 'first-day-of-week':
                return 'number'
            case 'mode':
                return 'string'
            /*case 'min-date':
            case 'max-date':
            case 'date-value':
                return 'date'*/
            default:
                return super.getPropertyType(name);
        }
    }

    template() {
        return `		
            <label style="position: relative;">	
                <span label>${this.label || ''}</span>

                ${this.mode === 'action' ?
                `<input input type="text" ${this.renderAttributes(['name', 'value', 'disabled', 'placeholder', 'type', 'readonly', 'required', 'zs-tabindex', 'invalid'])}>
                <a class="zs-icon zs-icon-close-circle-fill zs-interactive-primary" clear href="#" onclick="return false;" hidden></a>
                <a action href="#" onclick="return false;" class="zs-icon zs-interactive-secondary zs-icon-calendar" tabindex=-1></a>`
                :
                `<span field class="zs-input-icon">
                    <input class="zs-input" type="text" input ${this.renderAttributes(['name', 'value', 'disabled', 'placeholder', 'type', 'readonly', 'required', 'zs-tabindex', 'invalid'])}/>
                    <a class="zs-icon zs-icon-calendar" href="#" onclick="return false;"></a>
                    <a class="zs-icon zs-icon-remove" tabindex="-1" clear href="#" onclick="return false;" ${!this.isClearVisible ? ` hidden` : ''}></a>
                </span>`
            }
            </label>
            <div class="zs-calendar" style="display:none" mode="${this.mode}">
            ${this.buildHeader()}
            ${this.buildCalendar(this.dateValue, true)}
            ${this.mode === "action" ? this.buildFooter() : ''}
            </div>
            `
    }

    render() {

        if (!this._isRendered) {
            super.render(); // Call Field's render first
        }

        this._isRendered = false;

        this.container = this.container || this.querySelector('.zs-calendar');

        if (this.mode === 'action') {
            this.classList.add('zs-action-field');
            this.setAttribute("action-icon", "calendar"); // So that styles attached to [action-icon] are applied

            this.headerEl.appendChild(this.yearEl.parentElement); // Swap year and month dropdowns for action mode
        } else {
            this.classList.add('zs-field');
        }

        this.classList.add('zs-date-picker');

        this.setCurrentCalendar();

        this.on(['click'], this, this.prevRoleEl, false, false);
        this.on(['click'], this, this.nextRoleEl, false, false);
        this.on(['click'], this, this.todayRoleEl, false, false);
        this.on(['change'], this, this.monthEl, false, false);
        this.on(['change'], this, this.yearEl, false, false);

        this.on(['keydown', 'click', 'change', 'focus'], this, this.inputElement, false, false);
        this.on(['keydown'], this, this.container);

        //Touchstart added for iPad safari.
        this.on(['click', 'touchstart'], this, document, false, false);

        // Hide on scroll
        this.toggleHideOnScroll();

        this._isRendered = true;
        this.readonly = true;
    }

    _setDefaults() {
        this._flippable = false;
        this._hideOnScroll = false;
        this._firstDayOfWeek = 0;
        this._months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
        this._daysOfWeek = ["Su", "M", "Tu", "W", "Th", "F", "Sa"];
        this._minDate = new Date(1980, 1, 1);
        this._maxDate = new Date((new Date()).getFullYear() + 10, (new Date()).getMonth(), (new Date()).getDate());
        this._dateValue = new Date();
        this._disabledDates = [];

        this._mode = "action";
    }

    onclick(event) {
        super.onclick(event);
        if (event.currentTarget === this) {
            return;
        }
        if (event.currentTarget === this.inputElement) {
            if (isVisible(this.container)) {
                this.hide();
            }
            else {
                this.show();
            }
        } else if (event.currentTarget === this.tableEl) {
            this.handleCellClick(event);
        } else if (event.currentTarget === this.prevRoleEl) {
            this.setMonth(this.displayDate.getMonth() - 1);
        } else if (event.currentTarget === this.nextRoleEl) {
            this.setMonth(this.displayDate.getMonth() + 1);
        } else if (event.currentTarget === this.todayRoleEl) {
            let today = new Date();
            this.setYear(today.getFullYear());
            this.setMonth(today.getMonth());
        } else {
            this.clickAnywhere(event);
        }

    }

    onchange(event) {

        super.onchange(event);
        if (event.currentTarget == this) {
            return;
        }
        if (event.currentTarget == this.monthEl) {
            var newMonth, newDate;
            newMonth = this.monthEl.value;
            if (this.displayDate.getMonth() != newMonth) {
                newDate = new Date(this.displayDate.getFullYear(), newMonth, 1);
                this.buildCalendar(newDate);
                this.setCurrentCalendar();
            }
        } else if (event.currentTarget == this.yearEl) {
            var newYear, newDate;
            newYear = this.yearEl.value;
            if (this.displayDate.getFullYear() != newYear) {
                newDate = new Date(newYear, this.displayDate.getMonth(), 1);
                this.buildCalendar(newDate);
                this.setCurrentCalendar();
            }
        } else if (event.currentTarget == this.inputElement) {
            if (isVisible(this.container)) {
                this.getDate();
                this.showCurrent();
            }
            this.fire('change', { bubbles: true });
        }
    }

    //Touchstart added for iPad safari.
    ontouchstart(event) {
        //click works for anchor in safari too.
        if (event.target === this.anchorEl) {
            return;
        }
        this.clickAnywhere(event);
    }
    onmousewheel(event) {
        this.clickAnywhere(event);
    }
    onscroll(event) {
        this.clickAnywhere(event);
    }
    onDOMMouseScroll(event) {
        this.clickAnywhere(event);
    }
    onfocus(event) {

    }
    topMonthTraversal(cellIndex) {
        this.prevRoleEl.click();
        for (var i = 8; i >= 3; i--) {  //to check cells/dates of given cellIndex in all rows/weeks, starting from bottom row/last week of month.
            if (this.tableEl.querySelector('tr:nth-child(' + i + ') td:nth-child(' + (cellIndex + 1) + '):not([notinmonth])')) {
                return this.tableEl.querySelector('tr:nth-child(' + i + ') td:nth-child(' + (cellIndex + 1) + ')');
            }
        }
    }
    nextMonthTraversal() {
        this.nextRoleEl.click();
        return this.tableEl.querySelector('td:not([notinmonth])'); //returns first "notinmonth" date       
    }
    bottomMonthTraversal(cellIndex) {
        this.nextRoleEl.click();
        return this.tableEl.querySelector('td:nth-child(' + (cellIndex + 1) + '):not([notinmonth])');//return first "notinmonth" date in same column
    }
    prevMonthTraversal() {
        this.prevRoleEl.click();
        for (var i = 7; i > 1; i--) { //to traverse all rows i.e weeks starting from bottom row/last week of month
            for (var j = 6; j >= 0; j--) {  // to traverse all cells/dates starting from last cell/date in given row/week respectively.
                if (!this.tableEl.rows[i].cells[j].hasAttribute('notinmonth')) {
                    return this.tableEl.rows[i].cells[j];
                }
            }
        }
    }
    findTopCell(cell) {
        var cellIndex = cell.cellIndex;
        if (cell.closest('tr').previousElementSibling.children[cellIndex]) {  //same column
            if (cell.closest('tr').previousElementSibling.children[cellIndex].hasAttribute('notinmonth') && !cell.closest('tr').previousElementSibling.children[cellIndex].hasAttribute('disabled')) {
                return this.topMonthTraversal(cellIndex);
            }
            return cell.closest('tr').previousElementSibling.children[cellIndex];
        }
        else {
            if (this.prevRoleEl.hasAttribute('disabled')) {  //min date
                return cell;
            }
            return this.topMonthTraversal(cellIndex);
        }
    }
    findNextCell(cell) {
        if (cell.nextElementSibling) {  // same row
            if (cell.nextElementSibling.hasAttribute('notinmonth') && !cell.nextElementSibling.hasAttribute('disabled')) {
                return this.nextMonthTraversal();
            }
            return cell.nextElementSibling;
        }
        if (cell.closest('tr').nextElementSibling) {  //last row check
            if (cell.closest('tr').nextElementSibling.querySelector('td:first-child[notinmonth]') && !cell.closest('tr').nextElementSibling.querySelector('td:first-child[disabled]')) {
                return this.nextMonthTraversal();
            }
            return cell.closest('tr').nextElementSibling.querySelector('td:first-child');
        }
        if (this.nextRoleEl.hasAttribute('disabled')) {  //max date
            return cell;
        }
        return this.nextMonthTraversal();
    }
    findBottomCell(cell) {
        var cellIndex = cell.cellIndex;
        if (cell.closest('tr').nextElementSibling) {  //same column
            if (cell.closest('tr').nextElementSibling.children[cellIndex].hasAttribute('notinmonth') && !cell.closest('tr').nextElementSibling.children[cellIndex].hasAttribute('disabled')) {
                return this.bottomMonthTraversal(cellIndex);
            }
            return cell.closest('tr').nextElementSibling.children[cellIndex];
        }
        else {
            if (this.nextRoleEl.hasAttribute('disabled')) {  //max date
                return cell;
            }
            return this.bottomMonthTraversal(cellIndex);
        }
    }
    findPrevCell(cell) {
        if (cell.previousElementSibling) { //same row
            if (cell.previousElementSibling.hasAttribute('notinmonth') && !cell.previousElementSibling.hasAttribute('disabled')) {
                return this.prevMonthTraversal();
            }
            return cell.previousElementSibling;
        };
        if (cell.closest('tr').previousElementSibling.firstElementChild) {  //empty row check            
            if (cell.closest('tr').previousElementSibling.querySelector('td:last-child[notinmonth]') && !cell.closest('tr').previousElementSibling.querySelector('td:last-child[disabled]')) {
                return this.prevMonthTraversal();
            }
            return cell.closest('tr').previousElementSibling.querySelector('td:last-child');
        };
        if (this.prevRoleEl.hasAttribute('disabled')) {  //min date
            return cell;
        }
        return this.prevMonthTraversal();
    }
    findCell(cell, direction) {
        var next, cellclone = cell;
        switch (direction) {
            case "top":
                next = this.findTopCell(cell);
                break;
            case "right":
                next = this.findNextCell(cell);
                break;
            case "bottom":
                next = this.findBottomCell(cell);
                break;
            case "left":
                next = this.findPrevCell(cell);
                break;
        }
        while (true) {
            if (next.hasAttribute('disabled')) {
                return cellclone;
            }
            else if (!next.classList.contains('zs-disabled')) {
                break;
            }
            else if (direction === 'top') {  //top
                next = this.findTopCell(next);
            }
            else if (direction === 'right') {  //right
                next = this.findNextCell(next);
            }
            else if (direction === 'bottom') {  //bottom
                next = this.findBottomCell(next);
            }
            else {  //left
                next = this.findPrevCell(next);
            }
        }
        return next;
    }
    onkeydown(event) {
        if (event.currentTarget === this.inputElement) {  //opens calendar
            if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
                event.preventDefault();
                event.stopPropagation();
                this.show();
                this.tableEl.querySelector('td:not([notinmonth]):not([disabled]):not(.zs-disabled)').focus();
            }
        }
        else if (event.target.tagName === 'TD') {  // selecting the date
            event.preventDefault();
            event.stopPropagation();
            switch (event.key) {
                case "Enter":
                case " ":
                case "Spacebar":
                    this.handleCellClick(event);
                    this.inputElement.focus();  //focus stays on input element after selecting date.
                    break;
                case "Tab":
                    if (event.shiftKey) { this.yearEl.focus(); }
                    else {
                        if (this.prevRoleEl.hasAttribute('disabled')) {
                            this.todayRoleEl.focus();
                        }
                        this.prevRoleEl.focus();
                    }
                    break;
                case "ArrowUp":
                case "Up":
                    this.findCell(event.target, 'top').focus();
                    break;
                case "ArrowRight":
                case "Right":
                    this.findCell(event.target, 'right').focus();
                    break;
                case "ArrowDown":
                case "Down":
                    this.findCell(event.target, 'bottom').focus();
                    break;
                case "ArrowLeft":
                case "Left":
                    this.findCell(event.target, 'left').focus();
                    break;
            }
        }
        else if (event.target === this.prevRoleEl) {
            if (event.key === "Tab") {
                event.preventDefault();
                event.stopPropagation();
                if (event.shiftKey) {
                    this.tableEl.querySelector('td:not([notinmonth]):not([disabled]):not(.zs-disabled)').focus();
                }
                else {
                    this.todayRoleEl.focus();
                }
            }
        }
        else if (event.target === this.todayRoleEl) {  //had to write this case to handle max and min dates
            if (event.key === "Tab") {
                event.preventDefault();
                event.stopPropagation();
                if (event.shiftKey) {
                    if (this.prevRoleEl.hasAttribute('disabled')) {
                        this.tableEl.querySelector('td:not([notinmonth]):not([disabled]):not(.zs-disabled)').focus();
                    }
                    else {
                        this.prevRoleEl.focus();
                    }
                }
                else {
                    if (this.nextRoleEl.hasAttribute('disabled')) {
                        this.monthEl.focus();
                    }
                    else { this.nextRoleEl.focus(); }
                }
            }
        }
        else if (event.target === this.nextRoleEl) {
            if (event.key === "Tab") {
                event.preventDefault();
                event.stopPropagation();
                if (event.shiftKey) { this.todayRoleEl.focus(); }
                else { this.monthEl.focus(); }
            }
        }
        else if (event.target === this.monthEl) {
            if (event.key === "Tab") {
                event.preventDefault();
                event.stopPropagation();
                if (event.shiftKey) {   //browser is focusing on input element on shift+tab. Hence had to tamper it.
                    if (this.nextRoleEl.hasAttribute('disabled')) {
                        this.todayRoleEl.focus();
                    }
                    else { this.nextRoleEl.focus(); }
                }
                else {
                    this.yearEl.focus();
                }
            }
        }
        else if (event.target === this.yearEl) {
            if (event.key === "Tab") {
                event.preventDefault();
                event.stopPropagation();
                if (event.shiftKey) { this.monthEl.focus(); }
                else {
                    this.tableEl.querySelector('td:not([notinmonth]):not([disabled]):not(.zs-disabled)').focus();
                }
            }
        }
        if (event.key === "Escape" || event.key === "Esc") {
            this.hide();
            this.inputElement.focus();
        }
    }

    get anchorEl() {
        return this.inputElement.parentElement.querySelector('a.zs-icon-calendar');
    }
    get monthEl() {
        return this.container.querySelector('select[name="month"]');
    }
    get yearEl() {
        return this.container.querySelector('select[name="year"]');
    }
    get tableEl() {
        return this.container.querySelector('table');
    }
    get headerEl() {
        return this.container ? this.container.querySelector('header') : this.root.querySelector('header');
    }
    get footerEl() {
        return this.container ? this.container.querySelector('footer') : this.root.querySelector('footer');
    }
    get prevRoleEl() {
        return this.container.querySelector('[role="prev"]');
    }
    get nextRoleEl() {
        return this.container.querySelector('[role="next"]');
    }
    get todayRoleEl() {
        return this.container.querySelector('[role="today"]');
    }

    handleCellClick(event) {
        let targetCell = event.target;
        if (targetCell.tagName === 'TD') {
            var newValue = new Date(targetCell.getAttribute('date'));

            let isDisabled = Boolean(this.disabledDates.map(this.getDateWithoutTime).map(Number).indexOf(+newValue) >= 0);

            if (!isDisabled) {
                this.setDate(newValue);
            }
        }
    }

    clickAnywhere(event) {

        if (event.target == this.inputElement) {
            return;
        }

        if (event.target === this.anchorEl) {
            if (!isVisible(this.container)) {
                this.show();
                return;
            }
        }

        if (!isVisible(this.container)) {
            return;
        }

        if (event.target !== document && isThing(event.target.closest('.zs-calendar'))) {
            return;
        }
        this.hide();
    }

    toggleHideOnScroll() {
        if (this.hideOnScroll) {
            this.on(['mousewheel', 'DOMMouseScroll', 'scroll'], this, document, false, false);
        }
        else {
            this.off(['mousewheel', 'DOMMouseScroll', 'scroll'], this, document);
        }
    }

    setContainerPosition() {
        this.container.style.top = '';
        this.container.style.left = '';
        if (!this.flippable) {
            if (!this.querySelector('.zs-calendar')) {
                this.appendChild(this.container);
            }
            if (this.container.parentElement == document.body) {

                this.container.parentElement.removeChild(this.container);
            }

            this.container.style.minWidth = '';
            return;
        }
        var rect = this.inputElement.getBoundingClientRect();
        if (this.container.parentElement !== document.body) {
            document.body.appendChild(this.container);
        }
        this.container.style.minWidth = 'auto';
        var left = rect.left + window.pageXOffset;
        var top = rect.top + rect.height + window.pageYOffset;

        if ((rect.top + rect.height + this.container.offsetHeight) > window.innerHeight) {
            top = top - rect.height - this.container.offsetHeight;
        }
        if ((rect.left + this.container.offsetWidth) > document.body.clientWidth) {
            left = left + rect.width - this.container.offsetWidth;
        }
        this.container.style.top = top + 'px';
        this.container.style.left = left + 'px';
        !this.flippable && (this.inputElement.offsetWidth > this.container.offsetWidth) ? this.container.style.width = this.inputElement.offsetWidth + 'px' : null;
    }

    //Creates the header of the calendar
    buildHeader() {
        var html = '', i;
        html = '<header>';

        this.mode !== 'action' ? html += '<a href="#" onclick="return false;" role="prev" class="zs-interactive-secondary"></a>' : '';

        html += '<span><select name="year">';
        for (i = this.minDate.getFullYear(); i <= this.maxDate.getFullYear(); i++) {
            html += '<option value="' + i + '"' + (i == this.dateValue.getFullYear() ? '" selected' : '') + '>' + i + '</option>';
        }
        html += '</select></span>';

        html += '<span><select name="month">';
        for (i = 0; i < 12; i++) {
            html += '<option value="' + i + '"' + (i == this.dateValue.getMonth() ? '" selected' : '') + '>' + this.months[i] + '</option>';
        }
        html += '</select></span>';

        this.mode !== 'action' ? html += '<a href="#" onclick="return false;" role="next" class="zs-interactive-secondary"></a>' : '';

        html += '</header>';

        return html;
    }

    //Builds entire month's calendar for the date provided
    buildCalendar(date, isTemplate) {
        var html = '', startDate, dayShift;

        // Set the current base month date 
        this.displayDate = date;

        // Start generating the HTML            
        html = '<tr>';

        // Weeks
        var arr = this.daysOfWeek.slice(0); // Clone array
        if (this.firstDayOfWeek) {
            // Shift array to the right
            var i = 0;
            while (i < this.firstDayOfWeek) {
                arr.push(arr.shift());
                i++;
            }
        }

        for (var i = 0; i < 7; i++) {
            html += '<th>' + arr[i] + '</th>';
        }

        html += '</tr><tr>';

        // Start date and how many days to show from previous month
        startDate = new Date(date.getFullYear(), date.getMonth(), 1);
        var month = startDate.getMonth();

        dayShift = startDate.getDay();
        dayShift = dayShift == 0 ? -7 : -dayShift - 7;
        dayShift += this.firstDayOfWeek % 7;

        startDate.setDate(startDate.getDate() + dayShift);

        var endOfWeekDate = new Date(startDate);
        endOfWeekDate.setDate(endOfWeekDate.getDate() + 6);

        // If entire first week is not in current month, shift start date by a week
        if (startDate.getMonth() != month && endOfWeekDate.getMonth() != month) {
            startDate.setDate(startDate.getDate() + 7);
        }

        // Build a days table
        for (i = 0; i < 42; i++) {

            if (i % 7 == 0) {
                html += '</tr><tr>';
            }
            html += this.buildCell(startDate, month);
            startDate.setDate(startDate.getDate() + 1);
        }

        // wrap up generating the day picker
        html = '<table>' + html + '</table>';
        if (isTemplate) {
            return html;
        }

        if (this.tableEl) {
            this.tableEl.parentElement.removeChild(this.tableEl);
        }

        this.headerEl.insertAdjacentHTML('afterend', html);
    }

    buildCell(startDate, month) {

        let isDisabled = Boolean(this.disabledDates.map(this.getDateWithoutTime).map(Number).indexOf(+startDate) >= 0);
        // converting array of objects(dates) to array of numbers(dates in milliseconds). For reference https://stackoverflow.com/questions/27450867/how-to-correctly-use-javascript-indexof-in-a-date-array
        return `
        <td date="${startDate.toString()}" ${startDate.getMonth() != month ? ' notInMonth' : ' tabindex=0'} ${(startDate > this.maxDate || startDate < this.minDate) ? ' disabled' : ''} ${(isDisabled) ? 'class="zs-disabled"' : ''}>${this.formatDay(startDate)}</td>
        `
    }

    getDateWithoutTime(dateVal) {

        return new Date(dateVal.getFullYear(), dateVal.getMonth(), dateVal.getDate());
    }
    /**
     * Generates HTML String for building footer of calendar. Contains prev/next and today links. Used in action mode.
     * @returns {string}
     */
    buildFooter() {
        let html =
            `<footer>
            <button class="zs-button zs-fab" role="prev"><span class="zs-icon zs-icon-arrow-left"></span></button>    
            <a href="#" onclick="return false;" role="today">Today</a>
            <button class="zs-button zs-fab" role="next"><span class="zs-icon zs-icon-arrow-right"></span></button>    
        </footer>
        `
        return html;
    }

    //shows the calendar and sets its functionalities.
    setCurrentCalendar() {
        this.showCurrent();

        this.on(['click'], this, this.tableEl, false, false);

        // Disable and enable next and prev buttons
        var newDate = new Date(this.displayDate.getTime()); // clone display date

        // Get first date of next  month
        newDate.setMonth(newDate.getMonth() + 1);
        newDate.setDate(1);
        if (this.maxDate && this.maxDate < newDate) {
            if (this.nextRoleEl) {
                this.nextRoleEl.setAttribute('disabled', 'true');
            }
        } else {

            if (this.nextRoleEl) {
                this.nextRoleEl.removeAttribute('disabled');
            }

        }

        newDate = new Date(this.displayDate.getTime()); // clone display date
        newDate.setDate(-1); // Get last date of previous month		

        if (this.minDate && this.minDate > newDate) {
            if (this.prevRoleEl) {
                this.prevRoleEl.setAttribute('disabled', 'true');
            }
        } else {
            if (this.prevRoleEl) {
                this.prevRoleEl.removeAttribute('disabled');
            }
        }
    }

    //Highlights today and selected date on calendar
    showCurrent() {
        var d = new Date();
        // Today
        d = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        if (this.container.querySelector('td[today]')) {
            this.container.querySelector('td[today]').removeAttribute('today');
        }
        if (this.container.querySelector('td[date="' + d.toString() + '"]')) {
            this.container.querySelector('td[date="' + d.toString() + '"]').setAttribute('today', '');
        }


        // Current
        if (this.container.querySelector('td[current]')) {
            this.container.querySelector('td[current]').removeAttribute('current');
        }
        d = this.dateValue;
        d = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        if (this.container.querySelector('td[date="' + d.toString() + '"]')) {
            this.container.querySelector('td[date="' + d.toString() + '"]').setAttribute('current', '');
        }
    }

    formatDay(date) {
        return date.getDate();
    }

    show() {
        this.fire('beforeOpen');
        this.getDate();
        if (this.displayDate != this.dateValue) {
            this.buildCalendar(this.dateValue);
            this.setCurrentCalendar();
        }
        this.container.style.display = 'block';
        this.setContainerPosition();
        this.fire('open');
    }

    //Gets date value from input box and builds calendar
    getDate() {
        var str = this.inputElement.value;
        if (str) {
            this.dateValue = this.stringToDate(str);
        } else {
            this.dateValue = this.dateValue || new Date();
        }

        // Set selected year and month
        this.monthEl.value = this.dateValue.getMonth();
        this.yearEl.value = this.dateValue.getFullYear();

        if (this.displayDate != this.dateValue) {
            this.buildCalendar(this.dateValue);
            this.setCurrentCalendar();
        }
    }

    //Sets calendar month
    setMonth(newMonth) {
        var newDate,
            newYear = this.displayDate.getFullYear();

        if (newMonth < 0) {
            newMonth = 11;
            newYear--;
        } else if (newMonth > 11) {
            newMonth = 0;
            newYear++;
        }

        newDate = new Date(newYear, newMonth, 1);

        if (this.displayDate.getMonth() != newMonth || this.displayDate.getFullYear() != newYear) {
            this.buildCalendar(newDate);
            this.setCurrentCalendar();
        }
        this.monthEl.value = newMonth;
        this.yearEl.value = newYear;
    }

    //Sets new date to calendar
    setDate(newValue) {
        this.dateValue = newValue;
        this.inputElement.value = this.dateToString(newValue);
        this.fire('change', { bubbles: true });
        this.showCurrent();
        this.hide();
    }
    dateToString(date) {
        if (typeof date === 'string') {
            date = this.stringToDate(date);
        }
        return date.toLocaleDateString('en-US').replace(/[^ -~]/g, ''); // Fix for IE11 based on http://stackoverflow.com/questions/21413757/tolocaledatestring-changes-in-ie11
    }
    stringToDate(str) {
        // !!!  Please be aware that a string parameter might be interpreted as a UTC date. And Browsers might not support some date formats and have issues with dates.					
        return new Date(str);
    }
    hide() {
        this.fire('beforeClose');
        this.container.style.display = 'none';
        this.fire('close');
    }
    setYear(newYear) {
        var newDate = new Date(newYear, this.displayDate.getMonth(), 1);
        if (newDate != this.displayDate) {
            this.buildCalendar(newDate);
            this.setCurrentCalendar();
        }
        this.yearEl.value = newYear;
    }
    cleanup() {

        this.off(['click', 'change', 'focus'], this, this.inputElement);
        this.off(['click'], this, this.tableEl);
        this.off(['click', 'touchstart', 'mousewheel', 'DOMMouseScroll', 'scroll'], this, document);
        this.off(['click'], this, this.prevRoleEl);
        this.off(['click'], this, this.nextRoleEl);
        this.off(['change'], this, this.monthEl);
        this.off(['change'], this, this.yearEl);

        if (this.container.parentElement == document.body) {
            this.container.parentElement.removeChild(this.container);
        }
        while (this.firstChild) {
            this.removeChild(this.firstChild);
        }

        this.container = undefined;
    }
    disconnectedCallback() {
        this.cleanup();
    }
    update() {
        this.cleanup();
        this._isRendered = false;
        this.render();
    }

    /**
     * @todo: Overridden from syncProp.m.js. To be removed once syncProps mixin supports date types.
     * @param {*} value Property value
     * @return {string} attribute value
     */
    propToAttrValue(value) {
        if (value instanceof Date) { return value.toString(); } // Date object value is stored as string in attribute
        if (value instanceof Array) { return value.toString(); } // Array values like daysoFWeek and months are stored as string in attribute
        return super.propToAttrValue(value);
    }
}
DatePicker.tagName = 'p';
DatePicker.is = 'zs-date-picker';

export default DatePicker;
